import { useEffect, useMemo, useState } from 'react'
import { BarChart3, CalendarDays } from 'lucide-react'
import { apiGet, cachedGet } from '../../lib/api.js'
import { usePersistedState } from '../../lib/persist.js'
import RevenueChart from '../../components/RevenueChart.jsx'
import RadarChart from '../../components/RadarChart.jsx'
import HeatmapYM from '../../components/HeatmapYM.jsx'
import {
  TH_MON_ABBR, TH_MON_FULL, MONTH_START, DAILY_LIMIT, ORANGE, BLUE, GREEN, PURPLE, SLATE, PALETTE, RADAR_PRESETS,
  pad, toISO, parseISO, daysBetween, fmt, enumMonths, bucketsFor, presetRange, comparePrevRange, compareLabel, comparePrevLabel, asList,
  Card, Segmented, Toggle, PresetBar, DateRange, FilterChips, Stat, Delta, ChartLoading, Notice, todayISO,
} from './statShared.jsx'

const rowKey = (gran, r) => (gran === 'month' ? `${r.year}-${pad(r.month)}` : r.date)
const TOOTHTAXI_BRANCH = '9' // mobile unit — off by default (skews comparisons)
// fetch the monthly data back ~10 years so the heatmap spans up to a decade
// (the line chart still starts at MONTH_START)
const DATA_START = `${Number(todayISO().slice(0, 4)) - 10}-01-01`

export default function RevenueStat({ excludeNhso = true, revenueType = null }) {
  const isOrtho = revenueType === 'ortho'
  const today = useMemo(() => todayISO(), [])  // Bangkok, not the browser clock
  const [branchName, setBranchName] = useState({})

  const pfx = revenueType ? `stat:rev:${revenueType}` : 'stat:rev:main'
  const init = useMemo(() => presetRange('1mo'), [])
  const [start, setStart] = usePersistedState(`${pfx}:start`, init[0])
  const [end, setEnd] = usePersistedState(`${pfx}:end`, init[1])
  const [preset, setPreset] = usePersistedState(`${pfx}:preset`, '1mo')
  const [metric, setMetric] = usePersistedState(`${pfx}:metric`, 'clinic')   // total | clinic
  const [mode, setMode] = usePersistedState(`${pfx}:mode2`, 'compare')        // sum | branch | compare (default เทียบ)
  const [radarCompare, setRadarCompare] = usePersistedState(`${pfx}:radarCompare2`, true)
  // store DISABLED branches → any newly-loaded branch is enabled by default
  // (key bumped to v2 so the new Tooth-Taxi-off default replaces stale saved state)
  const [branchOff, setBranchOff] = usePersistedState(`${pfx}:branchOff2`, () => new Set([TOOTHTAXI_BRANCH]))

  const [topRows, setTopRows] = useState(null)
  const [topState, setTopState] = useState({ loading: true, err: '' })
  const [loadedGran, setLoadedGran] = useState(null)
  const [cmpRows, setCmpRows] = useState(null)
  const [cmpState, setCmpState] = useState({ loading: false, err: '' })
  const [cmpLoadedGran, setCmpLoadedGran] = useState(null)

  const [bottomView, setBottomView] = usePersistedState(`${pfx}:bottomView`, 'line')  // line | heatmap (graph 2)
  const [bottomLineOff, setBottomLineOff] = usePersistedState(`${pfx}:bottomLineOff`, () => new Set(['total'])) // total hidden by default
  const [heatMetric, setHeatMetric] = usePersistedState(`${pfx}:heatMetric`, 'clinic') // total | clinic | doctor
  const [monthRows, setMonthRows] = useState(null)
  const [monthState, setMonthState] = useState({ loading: true, err: '' })

  const [orthoTopRows, setOrthoTopRows] = useState(null)
  const [orthoTopState, setOrthoTopState] = useState({ loading: false, err: '' })
  const [orthoLoadedGran, setOrthoLoadedGran] = useState(null)

  const rangeInvalid = end < start
  const rangeDays = daysBetween(start, end) + 1
  const gran = rangeDays > DAILY_LIMIT ? 'month' : 'day'
  const endpoint = gran === 'month' ? '/api/revenue/monthly' : '/api/revenue/daily'
  const isRadar = RADAR_PRESETS.includes(preset)
  const wantCompare = isRadar ? radarCompare : mode === 'compare'
  const topReady = topRows != null && loadedGran === gran
  const cmpReady = cmpRows != null && cmpLoadedGran === gran
  const orthoTopReady = orthoTopRows != null && orthoLoadedGran === gran

  const applyPreset = (key) => { const [s, e] = presetRange(key); setStart(s); setEnd(e); setPreset(key) }

  useEffect(() => {
    cachedGet('/api/branches')
      .then((br) => setBranchName(Object.fromEntries(asList(br).map((b) => [String(b.id ?? b.branch_id), b.name || b.branch_name || `สาขา #${b.id}`]))))
      .catch(() => {})
  }, [])

  useEffect(() => {
    if (rangeInvalid) return
    let alive = true
    setTopState({ loading: true, err: '' })
    apiGet(endpoint, { start_date: start, end_date: end, branch_id: 0, exclude_nhso: excludeNhso, type: revenueType })
      .then((rows) => { if (alive) { setTopRows(Array.isArray(rows) ? rows : []); setLoadedGran(gran); setTopState({ loading: false, err: '' }) } })
      .catch((e) => { if (alive) { setTopRows([]); setLoadedGran(gran); setTopState({ loading: false, err: e.message || 'โหลดข้อมูลไม่สำเร็จ' }) } })
    return () => { alive = false }
  }, [endpoint, start, end, rangeInvalid, gran, excludeNhso, revenueType])

  useEffect(() => {
    if (!wantCompare || rangeInvalid) { setCmpRows(null); return }
    let alive = true
    const [ps, pe] = comparePrevRange(preset, start, end)
    setCmpState({ loading: true, err: '' })
    apiGet(endpoint, { start_date: ps, end_date: pe, branch_id: 0, exclude_nhso: excludeNhso, type: revenueType })
      .then((rows) => { if (alive) { setCmpRows(Array.isArray(rows) ? rows : []); setCmpLoadedGran(gran); setCmpState({ loading: false, err: '' }) } })
      .catch((e) => { if (alive) { setCmpRows([]); setCmpLoadedGran(gran); setCmpState({ loading: false, err: e.message || 'โหลดข้อมูลไม่สำเร็จ' }) } })
    return () => { alive = false }
  }, [wantCompare, preset, endpoint, start, end, rangeInvalid, gran, excludeNhso, revenueType])

  useEffect(() => {
    let alive = true
    setMonthState({ loading: true, err: '' })
    apiGet('/api/revenue/monthly', { start_date: DATA_START, end_date: today, branch_id: 0, exclude_nhso: excludeNhso, type: revenueType })
      .then((rows) => { if (alive) { setMonthRows(Array.isArray(rows) ? rows : []); setMonthState({ loading: false, err: '' }) } })
      .catch((e) => { if (alive) { setMonthRows([]); setMonthState({ loading: false, err: e.message || 'โหลดข้อมูลไม่สำเร็จ' }) } })
    return () => { alive = false }
  }, [today, excludeNhso, revenueType])

  // ortho attach revenue — top range
  useEffect(() => {
    if (!isOrtho || rangeInvalid) { setOrthoTopRows(null); setOrthoLoadedGran(null); return }
    let alive = true
    setOrthoTopState({ loading: true, err: '' })
    const ep = gran === 'month' ? '/api/revenue/monthly' : '/api/revenue/daily'
    apiGet(ep, { start_date: start, end_date: end, branch_id: 0, exclude_nhso: excludeNhso, type: 'ortho_attach' })
      .then((rows) => { if (alive) { setOrthoTopRows(Array.isArray(rows) ? rows : []); setOrthoLoadedGran(gran); setOrthoTopState({ loading: false, err: '' }) } })
      .catch((e) => { if (alive) { setOrthoTopRows([]); setOrthoLoadedGran(gran); setOrthoTopState({ loading: false, err: e.message || 'โหลดข้อมูลไม่สำเร็จ' }) } })
    return () => { alive = false }
  }, [isOrtho, gran, start, end, rangeInvalid, excludeNhso])

  const branches = useMemo(() => {
    const ids = new Set()
    for (const r of topRows || []) ids.add(String(r.branch))
    for (const r of monthRows || []) ids.add(String(r.branch))
    for (const r of cmpRows || []) ids.add(String(r.branch))
    return [...ids].filter(Boolean).sort((a, b) => Number(a) - Number(b))
      .map((id, i) => ({ id, label: branchName[id] || `สาขา #${id}`, color: PALETTE[i % PALETTE.length] }))
  }, [topRows, monthRows, cmpRows, branchName])

  const branchEnabled = useMemo(() => new Set(branches.filter((b) => !branchOff.has(b.id)).map((b) => b.id)), [branches, branchOff])

  const metricKey = metric === 'total' ? 'priceTotal' : metric === 'clinic' ? 'priceClinic' : 'priceDoctor'
  const metricColor = metric === 'total' ? ORANGE : metric === 'clinic' ? BLUE : PURPLE
  const metricLabel = metric === 'total' ? 'รวมทั้งหมด' : metric === 'clinic' ? 'ส่วนคลินิก' : 'ส่วนหมอ'
  const effectiveEnabled = branchEnabled // star chart now respects the branch chips too
  const orthoTopSum = useMemo(() => (orthoTopRows || []).reduce((s, r) => s + (r[metricKey] || 0), 0), [orthoTopRows, metricKey])

  const sumByBucket = (rows) => {
    const acc = {}
    for (const r of rows || []) {
      if (!effectiveEnabled.has(String(r.branch))) continue
      const k = rowKey(gran, r)
      acc[k] = (acc[k] || 0) + (r[metricKey] || 0)
    }
    return acc
  }

  const top = useMemo(() => {
    if (rangeInvalid || !topReady) return { data: [], series: [] }
    const buckets = bucketsFor(gran, start, end)

    // pre-bucket ortho attach revenue (no branch filtering — it's a plain total overlay)
    const oAcc = {}
    if (isOrtho && orthoTopRows) {
      for (const r of orthoTopRows) { const k = rowKey(gran, r); oAcc[k] = (oAcc[k] || 0) + (r[metricKey] || 0) }
    }

    if (mode === 'branch') {
      const shown = branches.filter((b) => effectiveEnabled.has(b.id))
      const series = shown.map((b) => ({ key: b.id, label: b.label, color: b.color }))
      const acc = {}
      for (const r of topRows) {
        const bid = String(r.branch)
        if (!effectiveEnabled.has(bid)) continue
        const k = rowKey(gran, r)
        ;(acc[k] || (acc[k] = {}))[bid] = (acc[k][bid] || 0) + (r[metricKey] || 0)
      }
      const data = buckets.map((bk) => {
        const row = { label: bk.label, full: bk.full }
        for (const s of series) row[s.key] = acc[bk.key]?.[s.key] || 0
        return row
      })
      return { data, series }
    }
    if (mode === 'compare') {
      const [ps, pe] = comparePrevRange(preset, start, end)
      const prevBuckets = bucketsFor(gran, ps, pe)
      const cur = sumByBucket(topRows), prv = sumByBucket(cmpReady ? cmpRows : null)
      const series = [
        { key: 'current', label: 'ช่วงนี้', color: metricColor },
        { key: 'previous', label: comparePrevLabel(preset), color: SLATE, dashed: true },
      ]
      if (isOrtho) series.push({ key: 'orthoAttach', label: 'ติดเครื่องมือ', color: GREEN })
      const data = buckets.map((bk, i) => {
        const row = { label: bk.label, full: bk.full, current: cur[bk.key] || 0, previous: prv[prevBuckets[i]?.key] || 0 }
        if (isOrtho) row.orthoAttach = oAcc[bk.key] || 0
        return row
      })
      return { data, series }
    }
    const acc = sumByBucket(topRows)
    const series = [{ key: 'value', label: metricLabel, color: metricColor }]
    if (isOrtho) series.push({ key: 'orthoAttach', label: 'ติดเครื่องมือ', color: GREEN })
    const data = buckets.map((bk) => {
      const row = { label: bk.label, full: bk.full, value: acc[bk.key] || 0 }
      if (isOrtho) row.orthoAttach = oAcc[bk.key] || 0
      return row
    })
    return { data, series }
  }, [topRows, cmpRows, topReady, cmpReady, mode, metric, gran, start, end, preset, rangeInvalid, branches, effectiveEnabled, isOrtho, orthoTopRows, metricKey])

  const radar = useMemo(() => {
    if (!isRadar || !topReady) return []
    const cur = {}, prv = {}
    for (const r of topRows) { const b = String(r.branch); cur[b] = (cur[b] || 0) + (r[metricKey] || 0) }
    if (radarCompare && cmpReady) for (const r of cmpRows) { const b = String(r.branch); prv[b] = (prv[b] || 0) + (r[metricKey] || 0) }
    return branches.filter((b) => branchEnabled.has(b.id))
      .map((b) => ({ key: b.id, label: b.label, color: b.color, value: cur[b.id] || 0, prev: prv[b.id] || 0 }))
      .filter((a) => a.value > 0 || (radarCompare && a.prev > 0))
  }, [isRadar, topRows, cmpRows, topReady, cmpReady, radarCompare, branches, branchEnabled, metricKey])
  const radarTotal = useMemo(() => radar.reduce((s, a) => s + a.value, 0), [radar])
  const radarPrevTotal = useMemo(() => radar.reduce((s, a) => s + (a.prev || 0), 0), [radar])
  const radarDelta = radarCompare && radarPrevTotal ? ((radarTotal - radarPrevTotal) / radarPrevTotal) * 100 : null

  const topSummary = useMemo(() => {
    const total = (rows) => (rows || []).reduce((s, r) => (effectiveEnabled.has(String(r.branch)) ? s + (r[metricKey] || 0) : s), 0)
    const current = total(topRows)
    if (mode !== 'compare') return { current }
    const previous = total(cmpRows)
    const delta = previous ? ((current - previous) / previous) * 100 : null
    return { current, previous, delta }
  }, [topRows, cmpRows, mode, metricKey, effectiveEnabled])

  const bottom = useMemo(() => {
    if (!monthRows) return []
    const acc = {}
    for (const r of monthRows) {
      if (r.year < 2000 || !effectiveEnabled.has(String(r.branch))) continue
      const k = `${r.year}-${pad(r.month)}`
      ;(acc[k] || (acc[k] = { total: 0, clinic: 0, doctor: 0 }))
      acc[k].total += r.priceTotal || 0
      acc[k].clinic += r.priceClinic || 0
      acc[k].doctor += r.priceDoctor || 0
    }
    let last = MONTH_START
    for (const r of monthRows) { if (r.year >= 2000) { const iso = `${r.year}-${pad(r.month)}-01`; if (iso > last) last = iso } }
    return enumMonths(MONTH_START, last > today ? last : today).map(({ year, month }) => {
      const k = `${year}-${pad(month)}`
      return { label: `${TH_MON_ABBR[month - 1]} ${String(year).slice(2)}`, full: `${TH_MON_FULL[month - 1]} ${year}`, total: acc[k]?.total || 0, clinic: acc[k]?.clinic || 0, doctor: acc[k]?.doctor || 0 }
    })
  }, [monthRows, effectiveEnabled, today])
  const bottomSeries = [{ key: 'total', label: 'รวมทั้งหมด', color: ORANGE }, { key: 'clinic', label: 'ส่วนคลินิก', color: BLUE }, { key: 'doctor', label: 'ส่วนหมอ', color: PURPLE }]
  const bottomSum = useMemo(() => bottom.reduce((a, d) => ({ total: a.total + d.total, clinic: a.clinic + d.clinic, doctor: a.doctor + d.doctor }), { total: 0, clinic: 0, doctor: 0 }), [bottom])
  // month × year revenue for heatmap — metric follows heatMetric selector
  const heatMetricKey = heatMetric === 'total' ? 'priceTotal' : heatMetric === 'clinic' ? 'priceClinic' : 'priceDoctor'
  const heatMetricColor = heatMetric === 'total' ? ORANGE : heatMetric === 'clinic' ? BLUE : PURPLE
  const heatCells = useMemo(() => {
    if (!monthRows) return []
    const acc = {}
    for (const r of monthRows) {
      if (r.year < 2000 || !effectiveEnabled.has(String(r.branch))) continue
      const k = `${r.year}-${r.month}`
      acc[k] = (acc[k] || 0) + (r[heatMetricKey] || 0)
    }
    return Object.entries(acc).map(([k, v]) => { const [y, m] = k.split('-').map(Number); return { year: y, month: m, value: v } })
  }, [monthRows, effectiveEnabled, heatMetricKey])
  const bottomSeriesActive = bottomSeries.filter((s) => !bottomLineOff.has(s.key))

  const toggleBranch = (id) => setBranchOff((prev) => { const n = new Set(prev); n.has(id) ? n.delete(id) : n.add(id); return n })

  return (
    <>
      <div className="mb-5">
        <FilterChips label="สาขา" items={branches} enabled={branchEnabled}
          onToggle={toggleBranch} onAll={() => setBranchOff(new Set())} onClear={() => setBranchOff(new Set(branches.map((b) => b.id)))} />
      </div>

      <div className="space-y-6">
        <Card
          icon={<CalendarDays size={18} strokeWidth={1.75} />}
          title="รายได้ตามช่วงเวลา"
          badge={isRadar ? 'เรดาร์รายสาขา' : gran === 'month' ? 'รายเดือน' : 'รายวัน'}
          subtitle={isRadar ? 'เปรียบเทียบรายได้แต่ละสาขาในช่วงที่เลือก' : gran === 'month' ? `ช่วงยาว (${rangeDays} วัน) — แสดงแบบรายเดือน` : 'แยกเป็นรายวัน'}
          right={<DateRange start={start} end={end} today={today} onStart={(v) => { setStart(v); setPreset('') }} onEnd={(v) => { setEnd(v); setPreset('') }} />}
        >
          <PresetBar preset={preset} onPick={applyPreset} />
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <Segmented value={metric} onChange={setMetric} options={[{ v: 'total', label: 'Total', dot: ORANGE }, { v: 'clinic', label: 'Clinic', dot: BLUE }]} />
            <span className="w-px h-5 bg-slate-200 mx-1" />
            {isRadar ? (
              <Toggle on={radarCompare} onClick={() => setRadarCompare((v) => !v)}>{compareLabel(preset)}</Toggle>
            ) : (
              <Segmented value={mode} onChange={setMode} options={[{ v: 'sum', label: 'รวม' }, { v: 'branch', label: 'แยกสาขา' }, { v: 'compare', label: compareLabel(preset) }]} />
            )}
          </div>

          {rangeInvalid ? (
            <Notice text="วันสิ้นสุดต้องไม่ก่อนวันเริ่มต้น" tone="warn" />
          ) : topState.loading || !topReady || (wantCompare && (cmpState.loading || !cmpReady)) || (isOrtho && (orthoTopState.loading || !orthoTopReady)) ? (
            <ChartLoading />
          ) : topState.err ? (
            <Notice text={topState.err} tone="error" />
          ) : isRadar ? (
            <>
              <div className="flex flex-wrap items-end gap-x-8 gap-y-2 mb-1">
                <Stat label={metricLabel} value={fmt(radarTotal)} color={metricColor} />
                {radarCompare && <Stat label={comparePrevLabel(preset)} value={fmt(radarPrevTotal)} color={SLATE} />}
                {radarDelta != null && <Delta pct={radarDelta} />}
                <span className="text-[12px] text-slate-400 pb-1">{radar.length} สาขา</span>
              </div>
              <RadarChart axes={radar} color={metricColor} prevColor={SLATE} compare={radarCompare} height={380} compact={false} />
            </>
          ) : (
            <>
              <div className="flex flex-wrap items-end gap-x-8 gap-y-2 mb-3">
                <Stat label={metricLabel} value={fmt(topSummary.current)} color={metricColor} />
                {isOrtho && mode !== 'branch' && <Stat label="ติดเครื่องมือ" value={fmt(orthoTopSum)} color={GREEN} />}
                {mode === 'compare' && (
                  <>
                    <Stat label={comparePrevLabel(preset)} value={fmt(topSummary.previous)} color={SLATE} />
                    {topSummary.delta != null && <Delta pct={topSummary.delta} />}
                  </>
                )}
                <span className="text-[12px] text-slate-400 pb-1">{top.data.length} {gran === 'month' ? 'เดือน' : 'วัน'}</span>
              </div>
              <RevenueChart data={top.data} series={top.series} height={310} showLegend={mode !== 'branch'} compact={false} />
            </>
          )}
        </Card>

        <Card icon={<BarChart3 size={18} strokeWidth={1.75} />} title="รายได้รายเดือน" subtitle="ทั้งหมด ตั้งแต่เริ่มเก็บข้อมูล"
          right={<Segmented value={bottomView} onChange={setBottomView} options={[{ v: 'line', label: 'กราฟเส้น' }, { v: 'heatmap', label: 'ฮีตแมป' }]} />}>
          {monthState.loading ? (
            <ChartLoading />
          ) : monthState.err ? (
            <Notice text={monthState.err} tone="error" />
          ) : (
            <>
              {bottomView === 'line' ? (
                <>
                  <p className="text-[11.5px] text-slate-400 mb-2">คลิกที่ตัวเลขเพื่อแสดง/ซ่อนแต่ละเส้น</p>
                  <div className="flex flex-wrap items-end gap-x-8 gap-y-2 mb-3">
                    {bottomSeries.map((s) => {
                      const hidden = bottomLineOff.has(s.key)
                      return (
                        <button key={s.key} onClick={() => setBottomLineOff((prev) => { const n = new Set(prev); n.has(s.key) ? n.delete(s.key) : n.add(s.key); return n })}
                          className={`text-left transition-opacity ${hidden ? 'opacity-35' : ''}`}>
                          <div className="flex items-center gap-1.5 text-[12px] text-slate-500">
                            <span className="w-2 h-2 rounded-full" style={{ background: s.color }} />{s.label}
                          </div>
                          <div className="text-[19px] font-semibold text-slate-900 tabular-nums leading-tight">
                            {fmt(bottomSum[s.key])}
                          </div>
                        </button>
                      )
                    })}
                    <span className="text-[12px] text-slate-400 pb-1">{bottom.length} เดือน</span>
                  </div>
                  <RevenueChart data={bottom} series={bottomSeriesActive.length ? bottomSeriesActive : bottomSeries} height={320} labelAll valueLabels />
                </>
              ) : (
                <>
                  <div className="flex flex-wrap items-center gap-2 mb-3">
                    <span className="text-[12px] text-slate-500">แสดง</span>
                    <Segmented value={heatMetric} onChange={setHeatMetric} options={[{ v: 'total', label: 'Total', dot: ORANGE }, { v: 'clinic', label: 'Clinic', dot: BLUE }, { v: 'doctor', label: 'Doctor', dot: PURPLE }]} />
                  </div>
                  <HeatmapYM cells={heatCells} color={heatMetricColor} />
                </>
              )}
            </>
          )}
        </Card>
      </div>
    </>
  )
}
